import { memo } from 'react';
import type { computeVoronoiCells } from '@4cm/core/tactics/overlays';

/**
 * §17 — Voronoi overlay for the 2D tactics board.
 *
 * The 2D pitch SVG (`PitchSvg`) uses the landscape mapping
 *   SVG-x = -world.z
 *   SVG-y =  world.x
 * so we apply the same swap when emitting polygon vertices. Cells
 * authored in world metres line up exactly with the pitch markings and
 * player tokens that share the mapping.
 *
 * The opacity prop is the only thing controlled by the HUD slider —
 * it bakes directly into the rgba fill so coaches can dial down the
 * cells to glance through to the underlying token positions.
 */
export type VoronoiLayer2DCells = ReturnType<typeof computeVoronoiCells>;

export interface VoronoiLayer2DProps {
  cells: VoronoiLayer2DCells;
  /** Caller-controlled opacity in [0, 1]. */
  opacity: number;
  /** When false, away-team cells are skipped at render time so the
   *  coach can focus on home's spacing while the opposition tokens
   *  remain on-pitch for reference. The underlying compute path still
   *  receives both teams' positions — borders between home/away regions
   *  stay accurate, only the away fill is hidden. Default true. */
  showAway?: boolean;
}

// Home green / away amber — same family as the player-token colour
// treatment (PlayerToken2D). Opacity is baked into the rgba so the
// parent doesn't need an extra `opacity` attribute on every polygon.
function fillFor(team: 'home' | 'away' | undefined, opacity: number): string {
  if (team === 'away') return `rgba(251, 191, 36, ${opacity})`;
  // Default home + untagged → green.
  return `rgba(74, 222, 128, ${opacity})`;
}

function VoronoiLayer2DInner({ cells, opacity, showAway = true }: VoronoiLayer2DProps) {
  if (!cells || cells.length === 0) return null;
  return (
    <g
      className="t2d-voronoi-layer"
      data-testid="t2d-voronoi-layer"
      pointerEvents="none"
      // Clipped to the pitch rectangle (defined in PitchSvg's <defs>).
      // Voronoi cells from the compute path already stay in-bounds, but
      // the convex-hull approximation can produce vertices that sit on
      // the touchline; clipping prevents any sub-pixel bleed past it.
      clipPath="url(#t2d-pitch-clip)"
    >
      {cells.map((cell, idx) => {
        if (!cell.vertices || cell.vertices.length < 3) return null;
        // Filter at render time only — compute already received both
        // teams, so the boundary of home's territory is correctly
        // shaped by where the away players stand even when their fill
        // is hidden.
        if (!showAway && cell.team === 'away') return null;
        // World (x, z) → SVG (-z, x) per the landscape mapping.
        const points = cell.vertices
          .map((v) => `${-v.z},${v.x}`)
          .join(' ');
        // Tinted fill only — outlines removed per coach feedback.
        return (
          <polygon
            key={idx}
            points={points}
            fill={fillFor(cell.team, opacity * 0.65)}
            data-team={cell.team ?? 'neutral'}
            data-testid={`t2d-voronoi-cell-${idx}`}
          />
        );
      })}
    </g>
  );
}

/** Memoised — cells are recomputed only when token positions change, so
 *  the parent typically hands the same array reference across renders
 *  driven by unrelated state (e.g. HUD chip toggles). */
export const VoronoiLayer2D = memo(VoronoiLayer2DInner);
